// src/data/legalContent.js
// Legal documents, ported from the mobile app and adapted for the web
// ("App / mobile application" → "Service / website"). Rendered by pages/Legal.js.
// Section shape: { h: heading, p: [paragraphs], ul: [bullets] }

export const LEGAL_ORDER = ["terms", "privacy", "eula", "content-policy", "refund"];

export const LEGAL_DOCS = {
  terms: {
    title: "Terms of Use",
    intro: [
      'Welcome to KarviAI. This User Agreement ("Agreement") governs your access to and use of the KarviAI website and services ("Service"). By using the Service, you agree to comply with and be bound by this Agreement. If you do not agree, please do not use the Service.',
    ],
    sections: [
      { h: "1. Eligibility", p: ["To use KarviAI, you must be at least 13 years old or meet the minimum age required by local laws. If you are under 18, you must obtain permission from a parent or legal guardian."] },
      { h: "2. Account Registration and Security", ul: [
        "You may need to create an account to access certain features",
        "You are responsible for maintaining the confidentiality of your account credentials",
        "You must provide accurate and complete information when registering",
        "You agree not to share your account or allow others to access it",
      ] },
      { h: "3. Purchases and Subscriptions", ul: [
        "KarviAI may offer purchases and subscription-based services",
        "Payments are processed by third-party providers and are subject to their terms",
        "Subscriptions automatically renew unless canceled before the renewal date",
        "Refunds are subject to our refund policy and the terms of the payment provider",
      ] },
      { h: "4. User Conduct", p: ["KarviAI offers AI chat and role-play with companion characters as well as AI image and video generation. By using the Service, you agree not to:"], ul: [
        "Violate any laws or regulations",
        "Generate, in chats, role-play, images, or videos, any content prohibited by our Content Policy (including sexual content involving minors, non-consensual content, real-person deepfakes, bestiality, incest, or extreme violence)",
        "Engage in fraudulent, deceptive, or harmful activities",
        "Use the Service to harass, abuse, or harm others",
        "Circumvent age verification, content filters, or safety systems",
        "Modify, reverse-engineer, or attempt to hack the Service",
        "Distribute unauthorized content or spam",
      ] },
      { h: "5. Content Ownership and Usage", ul: [
        "You retain ownership of any content you create or upload to the Service",
        "Subject to this Agreement and our Content Policy, images and videos you generate through the Service are yours to use, and you are responsible for the prompts you submit",
        "By using the Service, you grant us a non-exclusive license to use, store, and display your content and generated media to provide and improve services",
        "We reserve the right to remove content, and to refuse or block any generation, that violates this Agreement or our Content Policy",
      ] },
      { h: "6. Termination and Suspension", p: ["We may suspend or terminate your access if you:"], ul: [
        "Violate this Agreement or applicable laws",
        "Engage in misconduct that disrupts the Service",
        "Attempt to exploit or misuse the Service",
      ] },
      { h: "7. Disclaimer of Warranties", ul: [
        'The Service is provided "as is" without warranties of any kind',
        "We do not guarantee uninterrupted or error-free operation of the Service",
        "Your use of the Service is at your own risk",
      ] },
      { h: "8. Limitation of Liability", p: [
        "We are not liable for any direct, indirect, incidental, or consequential damages arising from your use of the Service.",
        "Our total liability to you will not exceed the amount you paid for the Service in the past 12 months.",
      ] },
      { h: "9. Changes to This Agreement", p: ["We may update this Agreement from time to time. Continued use of the Service after updates constitutes acceptance of the revised terms."] },
      { h: "10. Contact Us", p: ["If you have any questions or concerns about this Agreement, please contact us at:"], email: true },
    ],
  },

  privacy: {
    title: "Privacy Policy",
    intro: [
      "Thank you for using KarviAI. Your privacy is important to us. This Privacy Policy explains how we collect, use, store, and protect your information when you use the KarviAI website and services.",
      "By using KarviAI, you agree to this Privacy Policy.",
    ],
    sections: [
      { h: "1. Information We Collect", ul: [
        "Email address",
        "Username",
        "Age confirmation information",
        "Subscription, credit, and payment-related information",
        "Device and browser information such as device type, operating system, IP address, and browser version",
        "Your chats and role-play conversations with AI characters",
        "Prompts you submit for image and video generation",
        "Images or audio you upload to be used in generation",
        "Images and videos generated for you through the Service",
        "Usage information such as activity, preferences, and feature usage",
      ], p: ["KarviAI does not store payment card details directly. Payments are securely processed through Razorpay or other payment providers if applicable."] },
      { h: "2. How We Use Your Information", ul: [
        "Create and manage your account",
        "Confirm age eligibility where required",
        "Provide AI chat, role-play, and image and video generation features",
        "Process your prompts and uploaded media to generate responses, images, and videos",
        "Process subscriptions, credits, and payments",
        "Provide customer support",
        "Send important updates related to your account or subscription",
        "Enforce our Content Policy and prevent abuse, fraud, and unauthorized access",
        "Improve performance and features",
      ] },
      { h: "3. How We Share Your Information", p: ["We do not sell your personal information.", "We may share limited information with trusted third parties only when necessary, including:"], ul: [
        "Third-party AI providers that process your prompts, chats, and uploaded media to generate text, image, and video content",
        "Cloud storage providers used to host your generated images and videos",
        "Payment providers such as Razorpay",
        "Analytics or hosting providers",
        "Legal authorities if required by law",
        "Service providers who help operate KarviAI",
      ] },
      { h: "4. Data Storage and Security", p: [
        "We use reasonable security measures to protect your information.",
        "However, no method of storage or transmission over the internet is completely secure, and we cannot guarantee absolute security.",
      ] },
      { h: "5. Your Rights", p: ["Depending on your location, you may have the right to:"], ul: [
        "Access your account information",
        "Update or correct your information",
        "Request account deletion",
        "Stop receiving promotional messages",
        "Request removal of stored personal information where applicable",
      ] },
      { h: "6. Third-Party Services", p: [
        "KarviAI may use third-party services for payments, analytics, hosting, and functionality.",
        "These third-party services may have their own privacy policies.",
      ] },
      { h: "7. Children's Privacy", p: [
        "KarviAI is not intended for users under the age of 13.",
        "We do not knowingly collect information from children under 13.",
      ] },
      { h: "8. Changes to This Privacy Policy", p: [
        "We may update this Privacy Policy from time to time.",
        "Any changes will be posted on this website.",
      ] },
      { h: "9. Contact Us", p: ["If you have any questions about this Privacy Policy, you may contact us at:"], email: true },
    ],
  },

  eula: {
    title: "End User License Agreement (EULA)",
    intro: ['This End User License Agreement ("EULA") is a legal agreement between you and KarviAI for the use of the KarviAI website and services.'],
    box: { type: "warning", text: "By using KarviAI, you acknowledge that you have read, understood, and agree to be bound by this End User License Agreement." },
    sections: [
      { h: "1. License Grant", p: ["KarviAI grants you a limited, non-exclusive, non-transferable, revocable license to use the Service for personal, non-commercial purposes subject to the terms of this EULA."] },
      { h: "2. Age Requirement", p: ["The Service may contain content suitable only for adults (18+). By accepting this EULA, you confirm that:"], ul: [
        "You are at least 13 years of age to use basic features",
        "You are at least 18 years of age to access mature content",
        "You have accurately provided your date of birth",
        "You understand age verification is permanent and cannot be changed",
      ] },
      { h: "3. AI-Generated Content", p: ["KarviAI lets you chat and role-play with AI companion characters and generate AI images and videos from your prompts. You understand and acknowledge that:"], ul: [
        "All chats, role-play conversations, images, and videos are generated by artificial intelligence",
        "AI characters are fictional and not real persons",
        "AI-generated images and videos are synthetic and may be inaccurate, imperfect, or unexpected",
        "Image and video generation consumes credits, which are used when you submit a generation request",
        "You are responsible for the prompts you submit and how you use the content you generate",
        "KarviAI does not provide professional advice of any kind",
      ] },
      { h: "4. Prohibited Uses", p: ["KarviAI permits adult content between consenting fictional adults. However, in chats, role-play, and image or video generation, you agree NOT to use the Service to:"], ul: [
        "Create sexual, nude, or suggestive content involving minors or anyone made to appear underage",
        "Generate non-consensual sexual content, including rape, assault, or drugged scenarios",
        "Create explicit or deepfake content depicting real, identifiable people or public figures without consent",
        "Generate bestiality, incest, or extreme violence, gore, or self-harm",
        "Create or share any other illegal content",
        "Harass, threaten, or harm real individuals",
        "Circumvent age verification, content filters, or safety systems",
        "Reverse engineer or modify the Service, or use automated systems to access the service",
      ], pAfter: ["These rules are described in full in our Content Policy, which forms part of this Agreement."] },
      { h: "5. Intellectual Property", p: ["All intellectual property rights in the Service and its content belong to KarviAI. You may not copy, modify, distribute, or create derivative works without express permission."] },
      { h: "6. User Content", p: ["You retain ownership of content you create. However, you grant KarviAI a license to use, store, and process your content to provide the service."] },
      { h: "7. Disclaimer of Warranties", p: ['THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTY OF ANY KIND. KARVIAI DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE.'] },
      { h: "8. Limitation of Liability", p: ["IN NO EVENT SHALL KARVIAI BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF THE SERVICE."] },
      { h: "9. Termination", p: ["This EULA is effective until terminated. KarviAI may terminate your access at any time for violation of these terms. Upon termination, you must cease all use of the Service."] },
      { h: "10. Governing Law", p: ["This EULA shall be governed by and construed in accordance with the laws of India, without regard to its conflict of law provisions."] },
      { h: "11. Contact", p: ["For questions about this EULA, please contact:"], email: true },
    ],
  },

  "content-policy": {
    title: "Content Policy",
    intro: ["This Content Policy explains what may and may not be created using KarviAI's image and video generation features. It applies to every prompt you submit. By generating content on KarviAI, you agree to follow this policy. Violations may result in blocked generations, content removal, or account suspension."],
    sections: [
      { h: "1. Adult Content", p: ["KarviAI is an 18+ platform and permits adult and mature themes between consenting, fictional adult characters. You must be of legal age in your jurisdiction to access these features."] },
      { h: "2. Strictly Prohibited Content", p: ["Regardless of the above, the following is never allowed and will be automatically blocked. Attempting to generate it may result in immediate account termination and reporting to authorities where required by law:"], ul: [
        "Any sexual or nude depiction of minors, or characters made to appear as minors (zero tolerance)",
        "Non-consensual content, including rape, sexual assault, or drugged/coerced scenarios",
        "Sexual or explicit depictions of real, identifiable people or public figures without consent, including deepfakes",
        "Bestiality or sexual content involving animals",
        "Incest or sexual content between close family members",
        "Extreme violence, gore, mutilation, self-harm, or suicide",
        "Content promoting illegal acts, including human trafficking, terrorism, weapons or explosives, and drug manufacturing",
      ] },
      { h: "3. How Enforcement Works", ul: [
        "Prompts are automatically screened before any image or video is generated",
        "Prompts that match prohibited categories are rejected and no content is produced",
        "Repeated or serious violations may lead to suspension or permanent termination",
      ] },
      { h: "4. Your Responsibility", ul: [
        "You are responsible for the prompts you submit and the content you generate",
        "Do not attempt to bypass, trick, or evade our safety systems",
        "Do not generate content that infringes others' rights or violates any law",
      ] },
      { h: "5. Reporting", p: ["If you encounter content that violates this policy, please report it so our team can review and take action."] },
      { h: "6. Changes to This Policy", p: ["We may update this Content Policy from time to time. Continued use of the Service after updates constitutes acceptance of the revised policy."] },
      { h: "7. Contact Us", p: ["If you have questions about this Content Policy, please contact us at:"], email: true },
    ],
  },

  refund: {
    title: "Refund & Cancellation Policy",
    intro: ["Thank you for using KarviAI. Please read our refund and cancellation policy carefully before purchasing any subscription."],
    box: { type: "info", text: "By subscribing to KarviAI Premium, you acknowledge and agree to this refund and cancellation policy." },
    sections: [
      { h: "1. No Refund Policy", p: [
        "All subscription purchases made on KarviAI are non-refundable.",
        "Once a subscription has been activated, no full or partial refunds will be provided for unused time, unused features, or accidental purchases.",
      ] },
      { h: "2. Credits & Generation", p: [
        "Credits are used to power features such as AI image and video generation.",
        "Credits and credit purchases are non-refundable. Credits are consumed at the time you submit a generation request and are not returned if you are unsatisfied with the result, since AI output is inherently unpredictable.",
        "If a generation fails due to a technical error on our side and no output is delivered, we may, at our discretion, re-credit the amount used.",
      ] },
      { h: "3. Subscription Cancellation", p: [
        "Users may cancel their subscription at any time before the next billing date.",
        "After cancellation, the subscription will remain active until the end of the current billing period.",
        "No further charges will be made after the current subscription period ends.",
      ] },
      { h: "4. Failed Payments", p: [
        "If a payment fails, KarviAI may retry the payment automatically based on the payment provider's retry policy.",
        "If payment continues to fail, your subscription may be downgraded or canceled.",
      ] },
      { h: "5. Contact Us", p: ["If you have any questions about this policy, please contact us at:"], email: true },
    ],
  },
};

export const LEGAL_EMAIL = "admin@karvi.com";
